import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Phone } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import LeadForm from "./LeadForm";
import SEO from "./SEO";
import { getPost, BLOG_POSTS } from "../blogData";
import { CONTACT_PHONE, CONTACT_PHONE_HREF } from "../mock";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPost(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-5 pt-24 bg-ls">
          <h1 className="font-display text-white text-5xl mb-4">Post not found</h1>
          <button className="btn-orange" onClick={() => navigate("/blog")}>
            Back to blog
          </button>
        </div>
        <Footer />
      </>
    );
  }

  const more = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${slug}`}
        image={post.image}
      />
      <Navbar />
      <main className="bg-ls">
        <article className="pt-28 md:pt-36 pb-16">
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#b7ab9b] hover:text-orange transition-colors font-cond tracking-wide mb-8"
            >
              <ArrowLeft className="w-4 h-4" /> Back to blog
            </Link>
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-orange">{post.category}</span>
            <h1 className="font-display text-white text-[9vw] sm:text-4xl md:text-5xl mt-3 mb-5">{post.title}</h1>
            <div className="flex items-center gap-4 text-sm text-[#9a8f80] mb-8">
              <span>By Dyan Holden</span>
              <span>·</span>
              <span>{post.date}</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {post.readTime}
              </span>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-5 md:px-8 mb-10">
            <div className="rounded-2xl overflow-hidden bg-black hero-frame">
              <img src={post.image} alt={post.title} className="w-full aspect-video object-cover" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <div className="space-y-5">
              {post.body.map((para, i) => (
                <p key={i} className="text-[17px] md:text-lg text-[#d7cdbf] leading-relaxed">
                  {para}
                </p>
              ))}
            </div>

            {/* Inline CTA */}
            <div
              className="mt-10 rounded-2xl p-7 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{ background: "var(--ls-surface)", border: "1px solid rgba(242,106,33,0.5)" }}
            >
              <div>
                <p className="font-cond font-extrabold text-2xl text-white uppercase tracking-wide">
                  Ready to stop missing jobs?
                </p>
                <p className="text-[#b7ab9b]">Website built for free. $597/mo. Cancel anytime.</p>
              </div>
              <a href={CONTACT_PHONE_HREF} className="btn-orange text-base shrink-0">
                <Phone className="w-4 h-4" /> {CONTACT_PHONE}
              </a>
            </div>
          </div>
        </article>

        {/* Lead form */}
        <section className="bg-ls-alt py-16 border-t" style={{ borderColor: "var(--ls-border)" }}>
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <h2 className="font-display text-white text-[8vw] sm:text-4xl mb-6 text-center">
              Get the system that books the jobs.
            </h2>
            <LeadForm source={`blog_${slug}`} compact />
          </div>
        </section>

        {/* More posts */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <h3 className="font-cond font-extrabold text-2xl text-white uppercase tracking-wide mb-6">Keep reading</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {more.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="card-dark overflow-hidden group flex flex-col">
                <div className="bg-black overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h4 className="font-cond font-bold text-lg text-white leading-tight group-hover:text-orange transition-colors">
                    {p.title}
                  </h4>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-orange text-sm font-cond font-bold uppercase tracking-wide">
                    Read <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
